/**
 * PATH: packages/drafting/src/panels/GeneratePanel.tsx
 * FILE_ID: DRAFTING-PANEL-GENERATE-001
 * PURPOSE: The AI surface (P3) — a prompt + optional hand sketch -> "Generate",
 *   a chain-of-thought progress line while the agentic loop runs, and a
 *   "Refine" bar to correct-by-prompt. Transport-agnostic: it calls the
 *   generate/refine handlers passed down from the host.
 * WHY: DRAFTING_STUDIO_SCOPE.md §7.2 — sketch+prompt -> validated scaled drawing,
 *   with visible generating/validating state and correct-by-prompt.
 */
import { useRef, useState } from 'react'
import { ImagePlus, Sparkles, Wand2, X } from 'lucide-react'

const field =
  'w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800'
const lbl = 'block text-xs font-medium text-slate-500 dark:text-slate-400'
const primaryBtn =
  'flex w-full items-center justify-center gap-2 rounded-md bg-indigo-600 px-2 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50'

export interface GeneratePanelProps {
  scale: string
  units: string
  generating: boolean
  status: string | null
  error: string | null
  canRefine: boolean
  onGenerate: (prompt: string, sketch: File | null) => void
  onRefine: (instruction: string) => void
  /** Show the sketch on the canvas as a trace-over background. */
  onBackground: (url: string | null) => void
  hasBackground: boolean
}

export function GeneratePanel(props: GeneratePanelProps) {
  const { scale, units, generating, status, error, canRefine, onGenerate, onRefine, onBackground, hasBackground } = props
  const [prompt, setPrompt] = useState('')
  const [instruction, setInstruction] = useState('')
  const [sketch, setSketch] = useState<File | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)

  const pickSketch = (file: File | null) => {
    setSketch(file)
    onBackground(file ? URL.createObjectURL(file) : null)
  }

  return (
    <section className="space-y-2.5 rounded-lg border border-indigo-100 bg-indigo-50/50 p-2.5 dark:border-indigo-900/40 dark:bg-indigo-950/20">
      <h3 className="flex items-center gap-1.5 px-1 text-xs font-semibold uppercase tracking-wide text-indigo-500">
        <Sparkles className="h-3.5 w-3.5" aria-hidden /> AI generate
      </h3>

      <label className="block space-y-1">
        <span className={lbl}>Describe the drawing</span>
        <textarea
          className={`${field} min-h-[64px] resize-y`}
          placeholder={`e.g. window threshold detail, sill 40mm, scale ${scale}`}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          disabled={generating}
        />
      </label>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          pickSketch(e.target.files?.[0] ?? null)
          e.target.value = ''
        }}
      />
      {sketch || hasBackground ? (
        <button
          type="button"
          onClick={() => pickSketch(null)}
          disabled={generating}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-white dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <X className="h-3.5 w-3.5" aria-hidden /> {sketch ? sketch.name : 'Remove sketch'}
        </button>
      ) : (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={generating}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-white dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
        >
          <ImagePlus className="h-3.5 w-3.5" aria-hidden /> Add hand sketch (optional)
        </button>
      )}

      <button
        type="button"
        className={primaryBtn}
        disabled={generating || (!prompt.trim() && !sketch)}
        onClick={() => onGenerate(prompt.trim(), sketch)}
      >
        {generating ? (
          <>
            <Sparkles className="h-4 w-4 animate-pulse" aria-hidden /> Working…
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" aria-hidden /> Generate ({scale} · {units})
          </>
        )}
      </button>

      {(status || generating) && (
        <p className="flex items-center gap-1.5 px-1 text-xs text-indigo-600 dark:text-indigo-300" role="status">
          {generating && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-indigo-500" aria-hidden />}
          {status || 'Working…'}
        </p>
      )}
      {error && (
        <p className="px-1 text-xs text-rose-600 dark:text-rose-400" role="alert">
          {error}
        </p>
      )}

      {canRefine && (
        <div className="space-y-1 border-t border-indigo-100 pt-2 dark:border-indigo-900/40">
          <span className={lbl}>Refine by prompt</span>
          <div className="flex gap-1.5">
            <input
              className={field}
              placeholder="e.g. add a 10mm chamfer"
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              disabled={generating}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && instruction.trim() && !generating) {
                  onRefine(instruction.trim())
                  setInstruction('')
                }
              }}
            />
            <button
              type="button"
              className="inline-flex shrink-0 items-center rounded-md border border-indigo-300 px-2 text-indigo-600 hover:bg-white disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300"
              disabled={generating || !instruction.trim()}
              onClick={() => {
                onRefine(instruction.trim())
                setInstruction('')
              }}
              aria-label="Refine drawing"
            >
              <Wand2 className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
